import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useToast } from '../../contexts/ToastContext';
import { formatPrice } from '../../utils/priceFormatter';
import BottomNav from '../../components/layouts/BottomNav';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items, cartTotal, cartCount, clearCart } = useCart();
  const { showSuccess, showError } = useToast();

  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (!token || !user) {
      showError('Please login to place an order');
      navigate('/login');
      return;
    }
  }, [navigate, showError]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    paymentMethod: 'cod'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (items.length === 0) {
      showError('Cart is empty');
      return;
    }

    // Basic validation
    const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'state', 'pincode'];
    const missingFields = requiredFields.filter(field => !formData[field].trim());
    
    if (missingFields.length > 0) {
      showError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    setIsSubmitting(true);

    try {
      // Create order data
      const orderData = {
        items: items.map(item => ({
          product_id: item.id,
          product_name: item.name,
          unit_price: item.price,
          quantity: item.quantity,
          total_price: item.price * item.quantity
        })),
        total_amount: cartTotal,
        customer_details: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          country: formData.country
        },
        payment_method: formData.paymentMethod,
        status: 'pending'
      };

      console.log('Sending order data:', orderData);

      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        showError('Please login to place an order');
        navigate('/login');
        return;
      }

      // Submit order to backend
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to place order';
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // If response is not JSON (e.g., HTML error page)
          if (response.status === 500) {
            errorMessage = 'Server error. Please try again later.';
          } else if (response.status === 404) {
            errorMessage = 'Service not available. Please try again later.';
          } else if (response.status === 401) {
            showError('Please login to place an order');
            navigate('/login');
            return;
          }
        }
        
        throw new Error(errorMessage);
      }

      const orderResult = await response.json();
      
      // Clear cart and show success
      clearCart();
      showSuccess('Order placed successfully!');
      
      // Navigate to confirmation page
      navigate(`/order-confirmation/${orderResult.order_number}`, {
        state: { orderDetails: orderResult }
      });

    } catch (error) {
      console.error('Order submission error:', error);
      showError(error.message || 'Failed to place order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div style={{ background: "#fff", minHeight: "100vh", paddingBottom: 74 }}>
        <div style={{
          display: "flex", alignItems: "center", padding: "18px 18px 10px 18px",
          borderBottom: "1px solid #f3f3f3", background: "#fff", position: "sticky", top: 0, zIndex: 10
        }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "none",
              border: "none",
              fontSize: 26,
              color: "#222",
              cursor: "pointer",
              lineHeight: 1,
              marginRight: 14
            }}
            aria-label="Back"
          >
            &larr;
          </button>
          <span style={{
            fontWeight: 700,
            fontSize: 22,
            color: "#232323"
          }}>
            Checkout
          </span>
        </div>

        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 20px",
          textAlign: "center"
        }}>
          <div style={{
            fontSize: 64,
            marginBottom: 20,
            color: "#ddd"
          }}>
            🛒
          </div>
          <h2 style={{
            fontSize: 24,
            fontWeight: 600,
            color: "#333",
            marginBottom: 12
          }}>
            Your cart is empty
          </h2>
          <p style={{
            fontSize: 16,
            color: "#666",
            marginBottom: 30
          }}>
            Add some products to checkout
          </p>
          <button
            onClick={() => navigate('/products')}
            style={{
              background: "#333333",
              color: "#ffffff",
              border: "none",
              borderRadius: 8,
              padding: "14px 24px",
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
              transition: "background-color 0.2s ease"
            }}
            onMouseEnter={(e) => e.target.style.background = "#555555"}
            onMouseLeave={(e) => e.target.style.background = "#333333"}
          >
            Browse Products
          </button>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", minHeight: "100vh", paddingBottom: 74 }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", padding: "18px 18px 10px 18px",
        borderBottom: "1px solid #f3f3f3", background: "#fff", position: "sticky", top: 0, zIndex: 10
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "none",
            border: "none",
            fontSize: 26,
            color: "#222",
            cursor: "pointer",
            lineHeight: 1,
            marginRight: 14
          }}
          aria-label="Back"
        >
          &larr;
        </button>
        <span style={{
          fontWeight: 700,
          fontSize: 22,
          color: "#232323"
        }}>
          Checkout
        </span>
      </div>

      <form onSubmit={handleSubmit} style={{ padding: "20px" }}>
        {/* Order Summary */}
        <div style={{
          background: "#f8f9fa",
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
          border: "1px solid #e9ecef"
        }}>
          <h3 style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#333",
            marginBottom: 16
          }}>
            Order Summary ({cartCount} items)
          </h3>
          
          {items.map((item) => (
            <div key={item.id} style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0",
              borderBottom: "1px solid #e9ecef"
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#333"
                }}>
                  {item.name}
                </div>
                <div style={{
                  fontSize: 12,
                  color: "#666"
                }}>
                  Qty: {item.quantity}
                </div>
              </div>
              <div style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#333"
              }}>
                {formatPrice(item.price * item.quantity)}
              </div>
            </div>
          ))}
          
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 0",
            borderTop: "2px solid #333",
            marginTop: 12
          }}>
            <span style={{
              fontSize: 16,
              fontWeight: 600,
              color: "#333"
            }}>
              Total
            </span>
            <span style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#333"
            }}>
              {formatPrice(cartTotal)}
            </span>
          </div>
        </div>

        {/* Billing Details */}
        <div style={{
          background: "#f8f9fa",
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
          border: "1px solid #e9ecef"
        }}>
          <h3 style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#333",
            marginBottom: 16
          }}>
            Billing Details
          </h3>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
                marginBottom: 6
              }}>
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  fontSize: 14,
                  outline: "none"
                }}
                required
              />
            </div>
            <div>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
                marginBottom: 6
              }}>
                Last Name *
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  fontSize: 14,
                  outline: "none"
                }}
                required
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
                marginBottom: 6
              }}>
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  fontSize: 14,
                  outline: "none"
                }}
                required
              />
            </div>
            <div>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
                marginBottom: 6
              }}>
                Phone *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  fontSize: 14,
                  outline: "none"
                }}
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: "block",
              fontSize: 14,
              fontWeight: 500,
              color: "#333",
              marginBottom: 6
            }}>
              Address *
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              rows={3}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #ddd",
                borderRadius: 6,
                fontSize: 14,
                outline: "none",
                resize: "vertical"
              }}
              required
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
                marginBottom: 6
              }}>
                City *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  fontSize: 14,
                  outline: "none"
                }}
                required
              />
            </div>
            <div>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
                marginBottom: 6
              }}>
                State *
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  fontSize: 14,
                  outline: "none"
                }}
                required
              />
            </div>
            <div>
              <label style={{
                display: "block",
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
                marginBottom: 6
              }}>
                Pincode *
              </label>
              <input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ddd",
                  borderRadius: 6,
                  fontSize: 14,
                  outline: "none"
                }}
                required
              />
            </div>
          </div>

          <div>
            <label style={{
              display: "block",
              fontSize: 14,
              fontWeight: 500,
              color: "#333",
              marginBottom: 6
            }}>
              Country
            </label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #ddd",
                borderRadius: 6,
                fontSize: 14,
                outline: "none"
              }}
            />
          </div>
        </div>

        {/* Payment Method */}
        <div style={{
          background: "#f8f9fa",
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
          border: "1px solid #e9ecef"
        }}>
          <h3 style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#333",
            marginBottom: 16
          }}>
            Payment Method
          </h3>
          
          <div>
            <label style={{
              display: "flex",
              alignItems: "center",
              fontSize: 14,
              color: "#333",
              cursor: "pointer"
            }}>
              <input
                type="radio"
                name="paymentMethod"
                value="cod"
                checked={formData.paymentMethod === 'cod'}
                onChange={handleInputChange}
                style={{ marginRight: 8 }}
              />
              Cash on Delivery (COD)
            </label>
          </div>
        </div>

        {/* Place Order Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            width: "100%",
            padding: "16px 20px",
            background: isSubmitting ? "#ccc" : "#333333",
            color: "#ffffff",
            border: "none",
            borderRadius: 8,
            fontSize: 16,
            fontWeight: 600,
            cursor: isSubmitting ? "not-allowed" : "pointer",
            transition: "background-color 0.2s ease"
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.target.style.background = "#555555";
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.target.style.background = "#333333";
            }
          }}
        >
          {isSubmitting ? 'Placing Order...' : 'Place Order'}
        </button>
      </form>

      <BottomNav />
    </div>
  );
};

export default CheckoutPage;
